import type { RuleRow } from './MagicFlowAdmin.types';

const prettyJson = (value: unknown) => JSON.stringify(value, null, 2);

export const parseJson = (raw: string) => {
  try {
    return { value: JSON.parse(raw) as Record<string, unknown>, error: null };
  } catch (error) {
    return {
      value: null,
      error: error instanceof Error ? error.message : 'Invalid JSON',
    };
  }
};

export const createEmptyRuleRow = (index = 0): RuleRow => ({
  key: `rule-${Date.now()}-${index}`,
  id: '',
  name: '',
  event: '',
  order: index + 1,
  actionId: '',
  actionType: 'create_workflow',
  actionInputsJson: '{}',
  conditionsJson: '',
});

export const ruleSetToRows = (ruleSet?: Record<string, unknown>): RuleRow[] => {
  const rules = Array.isArray(ruleSet?.rules) ? ruleSet.rules : [];

  return rules.map((rule: any, index) => ({
    key: rule?.id || `rule-${index}`,
    id: String(rule?.id || ''),
    name: String(rule?.name || ''),
    event: String(rule?.trigger?.event || ''),
    order: Number(rule?.order || index + 1),
    actionId: String(rule?.action?.id || ''),
    actionType: String(rule?.action?.type || ''),
    actionInputsJson: prettyJson(rule?.action?.inputs || {}),
    conditionsJson: rule?.conditions ? prettyJson(rule.conditions) : '',
  }));
};

export const rowsToRuleSet = (rows: RuleRow[]) => {
  const rules = rows.map((row) => {
    const actionInputsParsed = parseJson(row.actionInputsJson || '{}');
    if (actionInputsParsed.error || !actionInputsParsed.value) {
      throw new Error(
        `Rule "${row.id || row.name}": invalid action inputs JSON`
      );
    }

    const rule: Record<string, unknown> = {
      id: row.id.trim(),
      name: row.name.trim(),
      trigger: {
        event: row.event.trim(),
      },
      order: Number(row.order),
      action: {
        id: row.actionId.trim(),
        type: row.actionType.trim(),
        inputs: actionInputsParsed.value,
      },
    };

    if (row.conditionsJson?.trim()) {
      const conditionsParsed = parseJson(row.conditionsJson);
      if (conditionsParsed.error || !conditionsParsed.value) {
        throw new Error(
          `Rule "${row.id || row.name}": invalid conditions JSON`
        );
      }
      rule.conditions = conditionsParsed.value;
    }

    return rule;
  });

  return { rules };
};

export const serializeRuleRows = (rows: RuleRow[]) =>
  JSON.stringify(
    rows.map((row) => ({
      id: row.id,
      name: row.name,
      event: row.event,
      order: row.order,
      actionId: row.actionId,
      actionType: row.actionType,
      actionInputsJson: row.actionInputsJson,
      conditionsJson: row.conditionsJson,
    }))
  );

export const stringifyJsonPretty = (value: unknown) => prettyJson(value);
